import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import axios from "axios";
import useImageModal from "../hooks/useImageModal";
import ImageModal from "./ImageModal";
import PublicTocPanel from "./PublicTocPanel";
import { extractPublicToc } from "../../utils/extractPublicToc";
import collapse from "../medias/collapse.png";

function PrivateLexicalPage({
  documentName,
  wrapperClass = "",
  /** Base CSS class of the inner scrollable container (the one styled in `privatePages.scss`).
   *  Override in onboarding/etc. to apply a divergent CSS without touching the regular private pages.
   *  The "-shifted" suffix is derived from this same base when the TOC opens. */
  containerClass = "private-container",
  onTocReady,
  onTocVisibleChange,
  /** When true, only body HTML/CSS are used — no TOC extraction, button, or panel (e.g. onboarding “existing doc” step). */
  hidePublicToc = false,
  /** When set with scrollCompletionActive, fires once when the reader scrolls the document through the end (sentinel visible). */
  onFullScrollComplete,
  scrollCompletionActive = false,
  /** Require at least this many seconds on the step (while scrollCompletionActive) in addition to scrolling to the end. 0 = scroll only. */
  minimumReadSeconds = 0,
  /** Hide the inline “reading time / scroll” banner (e.g. onboarding shows countdown in nav instead). */
  hideReadProgressMeta = false,
  /** Called with progress payload whenever scroll/time state changes; null when inactive. */
  onReadProgressReport,
}) {
  const STYLE_ID = `style-${documentName}`;

  const { modalIsOpen, selectedImageSrc, openModal, closeModal } =
    useImageModal();

  const privateContainerRef = useRef(null);
  /** Scrollable container (defaults to `.private-container`, see `containerClass` prop) — IntersectionObserver must use this as `root` so “end of document” matches in-panel scrolling (not the window). */
  const readScrollRootRef = useRef(null);
  const readSentinelRef = useRef(null);
  const readCompleteNotifiedRef = useRef(false);

  const [privateContent, setPrivateContent] = useState("");
  const [scrollReachedEnd, setScrollReachedEnd] = useState(false);
  const [elapsedReadSeconds, setElapsedReadSeconds] = useState(0);
  const [showPublicToc, setShowPublicToc] = useState(false);
  const [toc, setToc] = useState([]);
  const [tocVisible, setTocVisible] = useState(false);

  useEffect(() => {
    if (!onTocReady) return;
    if (!privateContainerRef.current) return;

    onTocReady({
      showPublicToc,
      toc,
      tocVisible,
      setTocVisible,
      containerRef: privateContainerRef,
    });
  }, [showPublicToc, toc, tocVisible]);

  // English comment: fetch private HTML + CSS + toc flag
  const fetchRteContents = async () => {
    try {
  const resp = await axios.get(
    "/api/admin/lexical/retrieve-member-html-document",
    {
      params: { documentName },
      withCredentials: true,
    }
  );

  const {
    htmlContent,
    cssPublic,
    contentHash,
    showPublicToc: tocFlag,
  } = resp.data.data;

  setPrivateContent(htmlContent || "");

  const showTocUi = !hidePublicToc && !!tocFlag;
  setShowPublicToc(showTocUi);

  if (showTocUi && htmlContent) {
    setToc(extractPublicToc(htmlContent));
  } else {
    setToc([]);
    setTocVisible(false);
  }

  let styleEl = document.getElementById(STYLE_ID);
  const prevHash = styleEl?.getAttribute("data-hash") || null;

  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = STYLE_ID;

    const meta = document.querySelector('meta[name="csp-nonce"]');
    const nonce = meta && meta.content;
    if (nonce) styleEl.setAttribute("nonce", nonce);

    document.head.appendChild(styleEl);
  }

  if (!contentHash || contentHash !== prevHash) {
    styleEl.textContent = cssPublic || "";
    if (contentHash) {
      styleEl.setAttribute("data-hash", contentHash);
    } else {
      styleEl.removeAttribute("data-hash");
    }
  }
} catch (err) {
  console.error("Error fetching member HTML+CSS:", err);
  setPrivateContent("");
}

  };

  useEffect(() => {
    fetchRteContents();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when document or TOC mode changes
  }, [documentName, hidePublicToc]);

  useEffect(() => {
    setScrollReachedEnd(false);
    setElapsedReadSeconds(0);
    readCompleteNotifiedRef.current = false;
  }, [documentName, privateContent]);

  useEffect(() => {
    if (!onFullScrollComplete || !scrollCompletionActive || minimumReadSeconds <= 0) return undefined;
    const id = window.setInterval(() => {
      setElapsedReadSeconds((s) => Math.min(minimumReadSeconds, s + 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [onFullScrollComplete, scrollCompletionActive, minimumReadSeconds]);

  useLayoutEffect(() => {
    if (!onFullScrollComplete || !scrollCompletionActive) return undefined;
    const sentinel = readSentinelRef.current;
    const rootEl = readScrollRootRef.current;
    if (!sentinel || !(rootEl instanceof Element)) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting && e.intersectionRatio > 0);
        if (hit) {
          setScrollReachedEnd(true);
          io.disconnect();
        }
      },
      {
        root: rootEl,
        rootMargin: "0px 0px -2px 0px",
        threshold: [0.05, 0.2, 0.6, 1],
      },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [onFullScrollComplete, scrollCompletionActive, privateContent]);

  useEffect(() => {
    if (!onFullScrollComplete || !scrollCompletionActive) return undefined;
    const el = readScrollRootRef.current;
    if (!el) return undefined;

    const epsilon = 16;
    /**
     * Never call synchronously on mount: before layout, scrollHeight can equal clientHeight so the
     * pane looks “at bottom” without scrolling — then validation would only need the timer.
     */
    const evaluateBottom = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (clientHeight <= 0 || scrollHeight <= 0) return;
      const atBottom = scrollTop + clientHeight >= scrollHeight - epsilon;
      setScrollReachedEnd(atBottom);
    };

    el.addEventListener("scroll", evaluateBottom, { passive: true });

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(evaluateBottom);
    });
    ro.observe(el);
    const stack = el.querySelector(".private-rte-read-stack");
    if (stack) ro.observe(stack);
    const inner = el.querySelector(".private-rte-content-container");
    if (inner) ro.observe(inner);

    const kick = requestAnimationFrame(() => {
      requestAnimationFrame(evaluateBottom);
    });

    return () => {
      cancelAnimationFrame(kick);
      ro.disconnect();
      el.removeEventListener("scroll", evaluateBottom);
    };
  }, [onFullScrollComplete, scrollCompletionActive, privateContent]);

  useEffect(() => {
    if (!onFullScrollComplete || !scrollCompletionActive) return;
    const min = Number(minimumReadSeconds) || 0;
    const timeOk = min === 0 || elapsedReadSeconds >= min;
    // Always require scroll-to-end in reading pane and minimum dwell when configured (both).
    if (!scrollReachedEnd || !timeOk) return;
    if (readCompleteNotifiedRef.current) return;
    readCompleteNotifiedRef.current = true;
    onFullScrollComplete();
  }, [
    onFullScrollComplete,
    scrollCompletionActive,
    scrollReachedEnd,
    elapsedReadSeconds,
    minimumReadSeconds,
  ]);

  useEffect(() => {
    if (typeof onReadProgressReport !== "function") return undefined;
    if (!scrollCompletionActive || !onFullScrollComplete) {
      onReadProgressReport(null);
      return undefined;
    }
    const min = Number(minimumReadSeconds) || 0;
    const rem = min > 0 ? Math.max(0, min - elapsedReadSeconds) : 0;
    onReadProgressReport({
      minimumTotalSeconds: min,
      minimumRemainingSeconds: min > 0 ? rem : null,
      scrollReachedEnd,
    });
    return undefined;
  }, [
    onReadProgressReport,
    scrollCompletionActive,
    onFullScrollComplete,
    minimumReadSeconds,
    elapsedReadSeconds,
    scrollReachedEnd,
  ]);

  const minRead = Number(minimumReadSeconds) || 0;
  const showReadHints = Boolean(onFullScrollComplete && minRead > 0);
  const readSecondsRemaining = Math.max(0, minRead - elapsedReadSeconds);

  // English comment: click image → modal
  useEffect(() => {
    const handleImageClick = (event) => {
      if (
        event.target.className.includes("e-rte-image") &&
        !event.target.className.includes("rte-emoji")
      ) {
        openModal(event.target.src);
      }
    };

    const c = privateContainerRef.current;
    if (c) {
      c.addEventListener("click", handleImageClick);
      return () => c.removeEventListener("click", handleImageClick);
    }
  }, [openModal]);

  // English comment: Trusted Types
  const trustedHTML = useMemo(() => {
    if (window.trustedTypes) {
      try {
        const policy = window.trustedTypes.createPolicy("pallasPrivatePages", {
          createHTML: (i) => i,
          createScript: (i) => i,
          createScriptURL: (i) => i,
        });
        return policy.createHTML(privateContent);
      } catch {
        return window.trustedTypes
          .createPolicy("pallasPrivatePages")
          .createHTML(privateContent);
      }
    }
    return privateContent;
  }, [privateContent]);

  useEffect(() => {
    // English comment: notify parent when toc visibility changes
    if (typeof onTocVisibleChange === "function") {
      onTocVisibleChange(tocVisible);
    }
  }, [tocVisible, onTocVisibleChange]);

  const handleTocToggle = () => {
    if (!tocVisible) {
      setTocVisible(true);
    } else {
      window.dispatchEvent(new Event("close-private-toc"));
    }
  };

  // ---------- CLASSIC PRIVATE LAYOUT ----------
  return (
    <div className={wrapperClass}>
      {showReadHints && !hideReadProgressMeta ? (
        <div className="private-rte-read-meta" aria-live="polite">
          <span className="private-rte-read-meta-item">
            {readSecondsRemaining > 0 ? (
              <>
                <span className="private-rte-read-meta-value">{readSecondsRemaining}</span>
                <span>s minimum reading time remaining</span>
              </>
            ) : (
              <span className="private-rte-read-meta-ok">Minimum reading time reached</span>
            )}
          </span>
          <span className="private-rte-read-meta-item">
            {scrollReachedEnd ? (
              <span className="private-rte-read-meta-ok">End of document reached</span>
            ) : (
              <span>Scroll through to the end of the document</span>
            )}
          </span>
        </div>
      ) : null}

      {tocVisible && showPublicToc && (
        <PublicTocPanel
          toc={toc}
          containerRef={privateContainerRef}
          onClose={() => setTocVisible(false)}
        />
      )}

      <div
        ref={readScrollRootRef}
        className={
          `${containerClass} margin-bottom-spacer` +
          (tocVisible ? ` ${containerClass}-shifted` : "")
        }
      >
        {showPublicToc && (
          <div
            className="private-toc-button-container"
            onClick={handleTocToggle}
          >
            <img
              src={collapse}
              alt="Toggle TOC"
              className={`private-toc-icon ${
                tocVisible ? "toc-open" : "toc-closed"
              }`}
            />
          </div>
        )}

        {onFullScrollComplete ? (
          <div className="private-rte-read-stack">
            <div
              ref={privateContainerRef}
              className="private-rte-content-container lexical-rendered-content"
              data-doc={documentName}
              role="document"
              dangerouslySetInnerHTML={{ __html: trustedHTML }}
            />
            <div
              ref={readSentinelRef}
              className="private-rte-read-sentinel"
              aria-hidden
            />
          </div>
        ) : (
          <div
            ref={privateContainerRef}
            className="private-rte-content-container lexical-rendered-content"
            data-doc={documentName}
            role="document"
            dangerouslySetInnerHTML={{ __html: trustedHTML }}
          />
        )}
      </div>

      <ImageModal
        isOpen={modalIsOpen}
        onClose={closeModal}
        src={selectedImageSrc}
        className="e-rte-image-custom-modal-content"
      />
    </div>
  );
}

export default PrivateLexicalPage;
